import { AppWorkerClient } from './AppWorkerClient';
import { WorkerBus } from './WorkerBus';
import type { TabSummary } from '../query_builder/types';

export type { TabSummary };

/**
 * TabManager: Manages the client-side state of tabs, syncing with the worker.
 * 
 * Implements Phase 1.3 of COHESIVE_PHASED_PLAN.md
 */
export class TabManager {
    private tabs: TabSummary[] = [];
    private activeTabId: string | null = null;
    private subscribers = new Set<() => void>();

    private client: AppWorkerClient;

    constructor(
        client: AppWorkerClient,
        bus: WorkerBus
    ) {
        this.client = client;
        bus.subscribe('TABS_UPDATED', (payload) => this.handleTabsUpdated(payload));

        // Initial fetch
        this.refresh();
    }

    private handleTabsUpdated(payload: { tabs: TabSummary[], active_tab_id: string }) {
        this.tabs = payload.tabs || [];
        this.activeTabId = payload.active_tab_id || null;
        this.notify();
    }

    async refresh() {
        try {
            // Parallel fetch
            const [tabs, activeId] = await Promise.all([
                this.client.list_tabs(),
                this.client.get_active_tab_id()
            ]);

            // Cast or validate if needed. 
            // Assuming worker returns objects compatible with TabSummary (string id, name, kind, etc.)
            this.tabs = (tabs as any[]) || [];
            this.activeTabId = activeId || null;
            this.notify();
        } catch (e) {
            console.error('[TabManager] Failed to refresh tabs', e);
        }
    }

    getTabs() { return this.tabs; }
    getActiveTabId() { return this.activeTabId; }
    getActiveTab() { return this.tabs.find(t => t.id === this.activeTabId); }

    subscribe(cb: () => void) {
        this.subscribers.add(cb);
        return () => this.subscribers.delete(cb);
    }

    private notify() {
        this.subscribers.forEach(cb => cb());
    }

    private static extractTabId(result: any): string | null {
        if (!result) return null;
        if (typeof result === 'string') return result;
        try {
            if (result instanceof Map) {
                const v = result.get('id');
                return v ? String(v) : null;
            }
        } catch { }
        if (typeof result === 'object') {
            const v = (result as any).id;
            return v ? String(v) : null;
        }
        return null;
    }

    // Actions
    async createSpreadsheet(name: string) {
        try {
            const res = await this.client.create_spreadsheet_tab(name);
            // `create_spreadsheet_tab` returns a Tab object (wasm) not a string id.
            // The worker also sets the new tab as active; we refresh to pick up that state.
            // If we can extract an id, we can additionally activate optimistically.
            const id = TabManager.extractTabId(res);
            if (id) {
                await this.activateTab(id);
            } else {
                await this.refresh();
            }
        } catch (e) {
            console.error('[TabManager] Failed to create spreadsheet tab', e);
            // Best-effort refresh to keep UI consistent.
            await this.refresh().catch(() => { });
        }
    }

    async createQuery(name: string): Promise<string | null> {
        try {
            const res = await this.client.create_query_tab(name);
            // `create_query_tab` returns a Tab object (wasm) not a string id.
            // The worker also sets the new tab as active; we refresh to pick up that state.
            // If we can extract an id, we can additionally activate optimistically.
            const id = TabManager.extractTabId(res);
            if (id) {
                await this.activateTab(id);
                return id;
            } else {
                await this.refresh();
                return null;
            }
        } catch (e) {
            console.error('[TabManager] Failed to create query tab', e);
            // Best-effort refresh to keep UI consistent.
            await this.refresh().catch(() => { });
            return null;
        }
    }

    async createDatasource(name: string): Promise<string | null> {
        try {
            const res = await this.client.create_datasource_tab(name);
            const id = TabManager.extractTabId(res);
            if (id) {
                await this.activateTab(id);
                return id;
            } else {
                await this.refresh();
                return null;
            }
        } catch (e) {
            console.error('[TabManager] Failed to create datasource tab', e);
            await this.refresh().catch(() => { });
            return null;
        }
    }

    async createDashboard(name: string): Promise<string | null> {
        try {
            const res = await this.client.create_dashboard_tab(name);
            const id = TabManager.extractTabId(res);
            if (id) {
                await this.activateTab(id);
                return id;
            } else {
                await this.refresh();
                return null;
            }
        } catch (e) {
            console.error('[TabManager] Failed to create dashboard tab', e);
            await this.refresh().catch(() => { });
            return null;
        }
    }

    async renameTab(id: string, name: string) {
        await this.client.rename_tab(id, name);
        await this.refresh();
    }

    async activateTab(id: string) {
        this.activeTabId = id; // Optimistic
        this.notify();
        await this.client.activate_tab(id);
        // Worker active tab state is now updated, but let's confirm
        await this.refresh();
    }
}
